import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, delay as rxDelay, finalize, map, of, tap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { NetworkMonitorService } from './network-monitor.service';

/**
 * Captures requests targeting `environment.baseApiUrl` and feeds them into the
 * `NetworkMonitorService`. Must be registered BEFORE the auth interceptor so
 * the captured entry reflects the original request (no bearer token in headers).
 */
export const networkMonitorInterceptor: HttpInterceptorFn = (req, next) => {
  if (environment.production) return next(req);
  if (!req.url.startsWith(environment.baseApiUrl)) return next(req);

  const monitor = inject(NetworkMonitorService);
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : String(Date.now()) + Math.random().toString(36).slice(2);

  const path = req.url.substring(environment.baseApiUrl.length) || '/';
  const headers: Record<string, string> = {};
  for (const k of req.headers.keys()) {
    const v = req.headers.get(k);
    if (v !== null) headers[k] = v;
  }

  const serializedBody = serializePayload(req.body);
  const startedAt = Date.now();

  monitor.add({
    id,
    method: req.method,
    url: req.url,
    path,
    requestBody: serializedBody,
    requestHeaders: headers,
    startedAt,
    state: 'pending',
    requestSize: estimateSize(serializedBody),
  });

  // Check for matching mock rules
  const mockRule = monitor.findMatchingMock(req.method, path, serializedBody);
  if (mockRule) {
    monitor.incrementMockHit(mockRule.id);
    const strategy = mockRule.strategy || 'mock';

    // Strategy: MAP — let the real request through, then transform the response
    if (strategy === 'map' && mockRule.mapFn?.trim()) {
      let settled = false;
      return next(req).pipe(
        map(event => {
          if (event instanceof HttpResponse) {
            settled = true;
            let body = serializePayload(event.body);
            let overriddenStatus: number | null = null;
            const setResponseStatusCode = (code: number) => { overriddenStatus = code; };
            try {
              const mapFn = new Function('response', 'request', 'setResponseStatusCode', mockRule.mapFn!) as
                (r: unknown, req: unknown, setStatus: (code: number) => void) => unknown;
              body = mapFn(body, serializedBody, setResponseStatusCode);
            } catch { /* invalid map function — use original response */ }
            const finalStatus = overriddenStatus ?? event.status;
            monitor.complete(id, finalStatus, 'Mapped', body, true);
            return event.clone({ body, status: finalStatus });
          }
          return event;
        }),
        catchError(error => {
          settled = true;
          const status = error instanceof HttpErrorResponse ? error.status : 0;
          const text = error instanceof HttpErrorResponse ? error.statusText : 'Network error';
          const errBody = error instanceof HttpErrorResponse ? error.error : String(error);
          monitor.complete(id, status, text, serializePayload(errBody), false);
          return throwError(() => error);
        }),
        finalize(() => { if (!settled) monitor.cancel(id); })
      );
    }

    // Strategy: MOCK — return a fake response
    const status = mockRule.mockStatus || 200;
    const responseBody = mockRule.mockResponse;
    const response = new HttpResponse({
      status,
      statusText: 'Mocked',
      body: responseBody,
      url: req.url,
    });
    const mockDelay = mockRule.delay ?? 50;
    setTimeout(() => {
      monitor.complete(id, status, 'Mocked', responseBody, true);
    }, mockDelay);
    return of(response);
  }

  let settled = false;

  return next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse) {
        settled = true;
        monitor.complete(id, event.status, event.statusText, serializePayload(event.body), false);
      }
    }),
    catchError(error => {
      settled = true;
      const status = error instanceof HttpErrorResponse ? error.status : 0;
      const text = error instanceof HttpErrorResponse ? error.statusText : 'Network error';
      const body = error instanceof HttpErrorResponse ? error.error : String(error);
      monitor.complete(id, status, text, serializePayload(body), false);
      return throwError(() => error);
    }),
    finalize(() => {
      if (!settled) {
        monitor.cancel(id);
      }
    })
  );
};

/** Convert a payload into something JSON-serializable for display & localStorage. */
function serializePayload(payload: unknown): unknown {
  if (payload === null || payload === undefined) return null;
  if (payload instanceof FormData) {
    const obj: Record<string, unknown> = {};
    payload.forEach((value, key) => {
      obj[key] = value instanceof File ? `[File: ${value.name} (${value.size} bytes)]` : value;
    });
    return obj;
  }
  if (payload instanceof Blob) return `[Blob ${payload.size} bytes]`;
  if (payload instanceof ArrayBuffer) return `[ArrayBuffer ${payload.byteLength} bytes]`;
  try {
    JSON.stringify(payload);
    return payload;
  } catch {
    return String(payload);
  }
}

/** Rough byte-size estimate for a serialized payload. */
function estimateSize(payload: unknown): number {
  if (payload === null || payload === undefined) return 0;
  if (typeof payload === 'string') return payload.length;
  try {
    return JSON.stringify(payload).length;
  } catch {
    return 0;
  }
}
